import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import HighlightComponent from 'components/HighlightComponent';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { formQueryStringFromObject, generatePagination } from 'helpers';
import { API_STATUS } from 'constants/app-constants';
import ServiceListPresentational from './service-list-presentational';
import { getDateFormat } from 'services/Utils';
import { useI18n } from 'hooks/useI18n';

const initialPageSize = 10;
const initialPageSizeOptions = [10, 15, 20];

const ServiceListFunctional = React.memo(() => {
	const { t } = useI18n();
	const serviceRedux = useSelector((state) => state?.serviceRedux);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const dispatch = useDispatch();

	const [serviceAddModal, setServiceAddModal] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [tableData, setTableData] = useState(serviceRedux?.services);
	const [editService, setEditService] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);

	const getServices = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId: globalRedux?.selectedBranch?.id,
		});
		let url = `${SERVER_IP}api/services?${string}`;
		dispatch(getApi('GET_SERVICES', url));
	}, [dispatch, globalRedux?.selectedBranch?.id]);

	useEffect(() => {
		getServices();
	}, [getServices]);

	useEffect(() => {
		generatePagination(tableData);
	}, [tableData]);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_SERVICE === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_SERVICE'));
			setSelectedRowKeys([]);
			doIt = true;
		}
		if (doIt) {
			getServices();
		}
	}, [globalRedux.apiStatus.DELETE_SERVICE, dispatch, getServices]);

	useEffect(() => {
		setTableData(serviceRedux?.services);
	}, [serviceRedux?.services]);

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((service) => {
			return (service?.serviceName || '')?.toLowerCase().includes(searchKey.toLowerCase());
		});
	}, [tableData, searchKey]);

	const handleDrawer = (rowData) => {
		setEditService(rowData);
		setServiceAddModal(true);
	};

	const handleAddService = () => {
		setEditService(null);
		setServiceAddModal(true);
	};

	const column = [
		{
			title: '#',
			dataIndex: 'serviceNumber',
			key: 'serviceNumber',
			// fixed: 'left',
			width: '5%',
			render: (value, record, index) => <div>{index + 1}</div>,
		},
		{
			title: t('services.columns.service_name'),
			dataIndex: 'serviceName',
			key: 'serviceName',
			sorter: (a, b) => a?.serviceName?.localeCompare(b?.serviceName),
			// fixed: 'left',
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape={true} textToHighlight={value} />
				</div>
			),
		},
		{
			title: t('services.columns.created_date'),
			dataIndex: 'created_at',
			align: 'left',
			width: '25%',
			render: (value) => getDateFormat(value),
		},
		{
			title: t('services.columns.action'),
			align: 'center',
			width: '10%',
			dataIndex: 'serviceName',
			render: (value, row, index) => {
				return (
					<Row justify="center">
						<Col className="edit_icon" onClick={() => handleDrawer(row)}>
							<EditOutlined />
						</Col>
					</Row>
				);
			},
		},
	];

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1) return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const tableLoading = useMemo(() => globalRedux.apiStatus.GET_SERVICES === API_STATUS.PENDING, [globalRedux.apiStatus]);

	const onSelectChange = (selectedRowKeys) => {
		console.log('🚀 ~ onSelectChange ~ selectedRowKeys:', selectedRowKeys);
		setSelectedRowKeys(selectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		// fixed: true,
	};

	const handleClose = useCallback(() => {
		setServiceAddModal(false);
		setEditService(null);
	}, [setServiceAddModal, setEditService]);

	return (
		<ServiceListPresentational
			{...{
				filteredData,
				column,
				tableLoading,
				rowSelection,
				selectedRowKeys,
				handleAddService,
				currentPage,
				pageSize,
				initialPageSizeOptions,
				initialPageSize,
				handleTableChange,
				setSearchKey,
				getStartingValue,
				getEndingValue,
				serviceAddModal,
				setServiceAddModal,
				refreshList: getServices,
				editService,
				handleClose,
			}}
		/>
	);
});

export default ServiceListFunctional;
